import { useEmbedContext } from '@thoughtspot/blink-context';
import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { ActionMenu, } from '@thoughtspot/radiant-react/widgets/action-menu';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { IconColor, IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { Popover } from '@thoughtspot/radiant-react/widgets/popover';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import cx from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DocumentationFrame } from './documentation-iframe';
import { GettingStartedModal } from './getting-started-modal';
import { HelpMenuState, HelpMenuTestIds } from './help-menu.const';
import styles from './help-menu.module.scss';
import { getDocumentationUrls, getHelpMenuItems } from './help-menu.util';
import { WhatsNewModal } from './whats-new-modal';
export const HelpMenu = ({ baseReleaseVersion, helpItems, inAppHelpUrl, isIntercomEnabled, isSaaSCluster, learningModule, onlineDocUrl, openExternalUrlInNewTab, releaseVersion, sendMixpanelEvent, showPendoHelpMenu, stopReplay, supportConfigItems, watchForFullReplay, isNavigationV3Enabled, }) => {
    const { isEmbedded } = useEmbedContext();
    const { t } = useTranslation();
    const [helpMenuState, setHelpMenuState] = useState(HelpMenuState.CLOSED);
    const [showReplayFrame, setShowReplayFrame] = useState(false);
    const helpPopoverRef = useRef(null);
    const navigationV3StyleProps = isNavigationV3Enabled
        ? {
            className: styles.helpMenuV3,
            iconColor: IconColor.WHITE,
        }
        : {};
    const { documentationUrl, whatsNewUrl, releaseNotesUrl, } = getDocumentationUrls(onlineDocUrl, inAppHelpUrl, baseReleaseVersion);
    const toggleHelpMenu = () => {
        setHelpMenuState(state => {
            if (state !== HelpMenuState.CLOSED) {
                return HelpMenuState.CLOSED;
            }
            return HelpMenuState.OPEN;
        });
        if (helpMenuState === HelpMenuState.CLOSED) {
            helpPopoverRef.current?.focus();
        }
        sendMixpanelEvent();
    };
    const onReplayStarted = () => {
        setShowReplayFrame(true);
    };
    const helpMenuItems = getHelpMenuItems(baseReleaseVersion, documentationUrl, helpItems, isEmbedded, isIntercomEnabled, isSaaSCluster, onReplayStarted, openExternalUrlInNewTab, releaseNotesUrl, setHelpMenuState, supportConfigItems, t, toggleHelpMenu, watchForFullReplay, whatsNewUrl);
    const openResourceCenter = () => {
        sendMixpanelEvent();
    };
    useEffect(() => {
        if (helpMenuState === HelpMenuState.OPEN) {
            document
                .querySelector(`#${helpMenuItems[0]?.items[0]?.id}`)
                ?.focus();
        }
    }, [helpMenuState]);
    const HelpMenuContent = useMemo(() => {
        if (helpMenuState === HelpMenuState.DOCUMENTATION) {
            return (React.createElement(DocumentationFrame, { documentationUrl: documentationUrl, setHelpMenuState: setHelpMenuState }));
        }
        const menuItemGroups = [
            ...helpMenuItems,
            {
                title: '',
                items: [
                    {
                        id: 'version',
                        customContent: (React.createElement(Typography, { variant: "p", className: styles.version }, `${t('Version')} ${releaseVersion}`)),
                        itemDisabled: true,
                    },
                ],
            },
        ];
        return (React.createElement("div", { "data-testid": HelpMenuTestIds.Popover },
            React.createElement(ActionMenu, { id: "help-menu", menuItemGroups: menuItemGroups, show: helpMenuState === HelpMenuState.OPEN, className: styles.actionMenu })));
    }, [helpMenuItems, helpMenuState]);
    if (showPendoHelpMenu) {
        return (React.createElement("div", { id: "resource-center-div" },
            React.createElement(Button, { onClick: openResourceCenter, icon: IconID.QUESTION_MARK, type: "secondary", "data-testid": HelpMenuTestIds.Button, ...navigationV3StyleProps })));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", null,
            showReplayFrame && React.createElement("div", { className: styles.replayFrame }),
            React.createElement(GlobalAlert.Toast, { "data-testid": "replay-message-toast", message: t('helpMenuItems.contactSupport.replayToast'), isVisible: showReplayFrame, showCloseButton: false, primaryAction: {
                    label: t('helpMenuItems.contactSupport.replayToastButton'),
                    onClick: async () => {
                        await stopReplay();
                        setShowReplayFrame(false);
                    },
                }, autoHide: false }),
            React.createElement(Button, { onClick: toggleHelpMenu, icon: IconID.QUESTION_MARK, type: "secondary", "data-testid": HelpMenuTestIds.Button, id: HelpMenuTestIds.Button, "aria-expanded": helpMenuState !== HelpMenuState.CLOSED, "aria-label": t('help_menu_aria_label'), "aria-haspopup": "menu", buttonRef: helpPopoverRef, ...navigationV3StyleProps }),
            React.createElement(Popover, { "aria-labelledby": HelpMenuTestIds.Button, "aria-hidden": helpMenuState === HelpMenuState.CLOSED, "aria-orientation": "vertical", className: cx(styles.profilePopover, {
                    [styles.documentationPopover]: helpMenuState === HelpMenuState.DOCUMENTATION,
                }), bottomOffset: 8, targetRef: helpPopoverRef, content: HelpMenuContent, isVisible: helpMenuState === HelpMenuState.OPEN ||
                    helpMenuState === HelpMenuState.DOCUMENTATION, align: "bottom-start", appendToBody: true, onClickOutside: () => {
                    setHelpMenuState(HelpMenuState.CLOSED);
                    helpPopoverRef.current.focus();
                }, onEscape: () => {
                    setHelpMenuState(HelpMenuState.CLOSED);
                    helpPopoverRef.current.focus();
                } }),
            helpMenuState === HelpMenuState.GETTING_STARTED && (React.createElement(GettingStartedModal, { learningModule: learningModule, setHelpMenuState: setHelpMenuState })),
            helpMenuState === HelpMenuState.WHATS_NEW && (React.createElement(WhatsNewModal, { setHelpMenuState: setHelpMenuState, frameUrl: whatsNewUrl, notesUrl: releaseNotesUrl, openExternalUrlInNewTab: openExternalUrlInNewTab })))));
};
//# sourceMappingURL=help-menu.js.map